import * as React from "react";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import { Box } from "@mui/system";
import { Step, StepButton, Stepper } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CreateInfo from "./CreateDataStoreComponent/CreateInfo";
import CreateInput from "./CreateDataStoreComponent/CreateInput";
import CreateOutput from "./CreateDataStoreComponent/CreateOutput";
import CreateSave from "./CreateDataStoreComponent/CreateSave";
import { useSelector } from "react-redux";
import {
  replaceCustomPlaceholders,
  // buildAndDispatchDbMetadata,
  validatePlaceholders,
} from "../DataStoreHelpers/dataStoreHelpers";
import {
  setCreateBizQueryFailed,
  setCreateBizQueryPayload,
  setCreateQueryFormData,
  setCreateQueryFormDataInput,
  setDbMetadataPayload,
  setDbMetaDataPostFailed,
  setHasError,
  setIsLoading,
} from "../../../../features/dataStoreReqSlice";
import { useDispatch } from "react-redux";
import CreateOutputError from "./CreateDataStoreComponent/CreateOutputError";
import { DataStoreReqEndPointLogic } from "../../../../logic/Admin/DataStoreRequest/DataStoreRequestEndPointLogic";
import { selectUserToken } from "../../../../features/userSlice";
import { buildAndDispatchCreate } from "../DataStoreHelpers/dataStoreHelpers";
import { fetchBizLayerQuery } from "../DataStoreHelpers/dataStoreHelpers";
import { useNavigate } from "react-router-dom";

export default function CreateDataStoreRequest() {
  const steps = ["Info", "Input", "Output", "Save"];
  const token = useSelector(selectUserToken);
  let react_env = import.meta.env.VITE_ENV;
  const navigate = useNavigate();

  const {
    hasError,
    createQueryFormData,
    createQueryFormDataInput,
    dbMetadataPayload,
    dbMetaDataPostFailed,
    createBizQueryPayload,
    isLoadingButtonCreate,
  } = useSelector((state: any) => state.dataStoreReq);
  const [activeStep, setActiveStep] = React.useState(0);
  const dispatch = useDispatch();
  const [completed, setCompleted] = React.useState<{ [k: number]: boolean }>(
    {}
  );
  const formData = useSelector(
    (state: any) => state.dataStoreReq.createQueryFormData
  );
  const oauthToken = useSelector(selectUserToken);
  const placeholders = validatePlaceholders(createQueryFormData.requestSql);
  const editableOutputData = useSelector(
    (state: any) => state.dataStoreReq.editableOutputValues
  );

  React.useEffect(() => {
    if (
      createQueryFormData?.requestSql &&
      createQueryFormDataInput &&
      Object.keys(createQueryFormDataInput).length > 0
    ) {
      const replaced = replaceCustomPlaceholders(
        createQueryFormData.requestSql,
        createQueryFormDataInput,
        react_env
      );

      dispatch(
        setDbMetadataPayload({
          database: createQueryFormData?.database?.value,
          query: replaced.replace(/\s+/g, " ").trim(),
        })
      );
    }
  }, [
    createQueryFormData?.requestSql,
    createQueryFormDataInput,
    createQueryFormData?.database?.value,
    react_env,
    dispatch,
  ]);

  React.useEffect(() => {
    if (activeStep === 3) {
      const payload = buildAndDispatchCreate(
        formData,
        placeholders,
        createQueryFormDataInput,
        editableOutputData
      );

      dispatch(setCreateBizQueryPayload(payload));
    }
    // eslint-disable-next-line
  }, [activeStep]);

  React.useEffect(() => {
    DataStoreReqEndPointLogic.getBizLayerQueryModules(
      token,
      dispatch,
      setIsLoading
    );
    return () => {
      dispatch(setHasError(false));
      dispatch(setIsLoading(false));
      dispatch(
        setCreateQueryFormData({
          queryName: "",
          database: null,
          queryDescription: "",
          requestSql: "",
          modules: null,
          type: null,
          confluence_url: null,
        })
      );
      dispatch(setCreateQueryFormDataInput({}));
      dispatch(setCreateBizQueryPayload({}));
      dispatch(setDbMetadataPayload({}));
      dispatch(setDbMetaDataPostFailed(false));
      dispatch(setCreateBizQueryFailed(false));
      dispatch(
        setCreateQueryFormDataInput({
          __replace: true,
          data: {},
        })
      );
    };
    // eslint-disable-next-line
  }, []);

  const handleNext = async () => {
    if (activeStep === 0) {
      const result = validatePlaceholders(formData.requestSql);
      if (!result.isValid) {
        dispatch(setHasError(true));
        return;
      }
      dispatch(setHasError(false)); // Clear any previous errors if validation passes
      setCompleted((prev) => ({ ...prev, [activeStep]: true })); // Mark step as completed
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else if (activeStep === 1) {
      const isSuccess = await DataStoreReqEndPointLogic.postMetaData(
        oauthToken,
        dbMetadataPayload,
        dispatch
      );
      if (isSuccess) {
        setCompleted((prev) => ({ ...prev, [activeStep]: true }));
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      } else {
        console.warn("Metadata post failed. Stay on step 1.");
        // optionally show error UI here
      }
    } else if (activeStep === 2) {
      setCompleted((prev) => ({ ...prev, [activeStep]: true })); // Mark step as completed
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else if (activeStep === 3) {
      const isSuccess = await DataStoreReqEndPointLogic.postBizLayerQuery(
        oauthToken,
        createBizQueryPayload,
        dispatch
      );
      if (isSuccess) {
        handleFinish();
      }
    }
  };

  const handleBack = () => {
    setCompleted((prev) => {
      const newCompleted = { ...prev };
      delete newCompleted[activeStep - 1]; // Remove checkmark from previous step
      return newCompleted;
    });
    setActiveStep((prevActiveStep) => Math.max(prevActiveStep - 1, 0)); // Prevent negative index
  };

  const handleStep = (step: number) => {
    setActiveStep(step);
  };

  const resetQueryFormState = () => {
    dispatch(setHasError(false));
    dispatch(setIsLoading(false));
    dispatch(
      setCreateQueryFormData({
        queryName: "",
        database: null,
        queryDescription: "",
        requestSql: "",
        modules: null,
        type: null,
        confluence_url: null,
      })
    );
    dispatch(setCreateQueryFormDataInput({}));
    dispatch(setCreateBizQueryPayload({}));
    // dispatch(setOutputValues({}));
    dispatch(setDbMetadataPayload({}));
    dispatch(setDbMetaDataPostFailed(false));
    dispatch(setCreateBizQueryFailed(false));
    // dispatch(setCreateQueryFormDataInput({}));
    // dispatch(setCreateQueryFormData({}));
    dispatch(
      setCreateQueryFormDataInput({
        __replace: true,
        data: {},
      })
    );
  };

  const handleFinish = () => {
    // Mark final step as completed and close dialog
    setCompleted((prev) => ({ ...prev, [activeStep]: true }));

    // Reset stepper after small delay
    setTimeout(() => {
      setActiveStep(0);
      setCompleted({});
    }, 300);

    // Reset all relevant state
    resetQueryFormState();

    // Re-fetch updated data
    fetchBizLayerQuery(token, dispatch, setIsLoading);
    const searchParams = new URLSearchParams(window.location.search);
    navigate({
      pathname: "/admin/queries/",
      search: searchParams.toString(),
    });
  };

  const isFormStep0Incomplete = Object.entries(createQueryFormData)
    .filter(([key]) => key !== "confluence_url") // exclude the key
    .some(([_, val]) => !val); // check if any value is falsy

  const isFormStep1Incomplete = (() => {
    const input = createQueryFormDataInput;
    const requiredFields = [
      "title",
      "description",
      "format",
      "order",
      "value_for_test",
    ];
    const validFormats = ["string", "number", "date", "array"];

    if (!input || Object.keys(input).length === 0) return true;

    const placeholderCount = placeholders?.placeholders?.length || 0;

    for (let i = 0; i < placeholderCount; i++) {
      const formatKey = `${i}-format`;
      const formatValue = input[formatKey];

      for (const field of requiredFields) {
        const key = `${i}-${field}`;
        const val = input[key];

        if (field === "format") {
          if (typeof val !== "string" || !validFormats.includes(val.trim())) {
            return true;
          }
          continue;
        }

        if (field === "value_for_test" && formatValue === "array") {
          if (
            !Array.isArray(val) ||
            val.some((item) => typeof item !== "string" || item.trim() === "")
          ) {
            return true;
          }
        } else {
          if (typeof val !== "string" || val.trim() === "") {
            return true;
          }
        }
      }
    }

    return false; // All fields valid
  })();

  const handleCancel = () => {
    resetQueryFormState();
    setActiveStep(0);
    setCompleted({});
    dispatch(setDbMetaDataPostFailed(false));
    const currentParams = new URLSearchParams(window.location.search);
    navigate({
      pathname: "/admin/queries/",
      search: currentParams.toString(),
    });
  };

  return (
    <Box>
      <DialogTitle id="alert-dialog-title">
        {"Add New Request - " +
          (activeStep === steps.length - 1
            ? "Review and Save"
            : steps[activeStep] +
              (activeStep === 1 || activeStep === 2 ? " Fields" : ""))}
      </DialogTitle>
      <DialogContent
        sx={{
          overflow: "hidden", // Prevent scrollbars inside dialog
          paddingBottom: "0px", // Optional: if spacing is pushing height
        }}
      >
        <Box sx={{ width: "100%", overflow: "hidden" }}>
          <Stepper nonLinear activeStep={activeStep}>
            {steps.map((label, index) => {
              const isStepDisabled = index > 0 && !completed[index - 1]; // disable step if previous step not completed

              return (
                <Step key={label} completed={completed[index]}>
                  <StepButton
                    disabled={isStepDisabled}
                    onClick={() => handleStep(index)}
                    sx={{
                      "& .MuiStepIcon-root": {
                        color:
                          activeStep === index &&
                          (dbMetaDataPostFailed || hasError)
                            ? "rgba(211, 47, 47, 1) !important"
                            : activeStep === index || completed[index]
                            ? "rgba(156, 39, 176, 1) !important"
                            : "rgba(0, 0, 0, 0.38) !important",
                      },
                      "&.Mui-active .MuiStepIcon-root, &.Mui-completed .MuiStepIcon-root":
                        {
                          color:
                            label === "Save" && hasError
                              ? "rgba(211, 47, 47, 1) !important"
                              : "rgba(156, 39, 176, 1) !important",
                        },
                    }}
                  >
                    {label}
                  </StepButton>
                </Step>
              );
            })}
          </Stepper>
        </Box>

        {/* Conditional rendering based on active step */}
        {activeStep === 0 && <CreateInfo />}

        {activeStep === 1 && (
          <Box>
            {!dbMetaDataPostFailed ? <CreateInput /> : <CreateOutputError />}
          </Box>
        )}

        {activeStep === 2 && (
          <Box>
            {/* will need to implement this after done */}
            <CreateOutput />
          </Box>
        )}

        {activeStep === 3 && <CreateSave />}
      </DialogContent>

      <DialogActions sx={{ padding: "24px 24px 24px 24px" }}>
        <Button
          onClick={handleCancel} // Function to close the dialog
          color="secondary"
        >
          Cancel
        </Button>
        <Button
          onClick={handleBack}
          variant="outlined"
          color="secondary"
          sx={activeStep === 0 ? { display: "none" } : {}}
          startIcon={<ArrowBackIcon />}
        >
          Back
        </Button>
        <Button
          disabled={
            (activeStep === 0 && isFormStep0Incomplete) ||
            (activeStep === 1 && isFormStep1Incomplete) ||
            isLoadingButtonCreate
          }
          onClick={handleNext}
          loading={isLoadingButtonCreate}
          color="secondary"
          variant="contained"
          endIcon={
            activeStep === steps.length - 1 ? (
              <SaveOutlinedIcon />
            ) : (
              <ArrowForwardOutlinedIcon />
            )
          }
          {...(activeStep === steps.length - 1
            ? { "data-tracker": "244" }
            : {})}
        >
          {activeStep === steps.length - 1
            ? "Save request to DataStore"
            : "Next"}
        </Button>
      </DialogActions>
    </Box>
  );
}
